// The insurance check's items, as the engineer's sheet (Session BBF). An insurer's inspector examined the lift between services and
// listed findings; ELE has not seen them. The engineer CHECKS each one and says what they found: present, not there or already fine,
// needs parts, needs a return visit, or could not be checked (with the reason).
//
// WHAT THIS SCREEN NEVER SHOWS, because the engineer's phone follows the platform's rules: no price, rate or charge of any kind (the
// check is never charged, and Session APF keeps money off every engineer screen), no customer detail, and nothing the office decides
// afterwards (a defect, an estimate). It never calls a finding a fault ELE found: it is the insurer's, in the insurer's own words.
//
// FAST BY CONSTRUCTION. Nothing is pre-answered: an unanswered item blocks the submit with a plain sentence, because a silence is not
// an answer and a "present" ticked by default would be a statement nobody made. One tap answers an item; words are asked only where
// the answer needs them (not there, could not check).
import type { EngineerInsuranceItem, InsuranceAnswerDraft, InsuranceOutcome, InsuranceSeverity } from "../../lib/offline/types";

export const INSURANCE_OUTCOME_CHOICES: { key: InsuranceOutcome; label: string }[] = [
  { key: "verified_present", label: "Present, as the insurer described" },
  { key: "not_present", label: "Not there, or already fine" },
  { key: "needs_parts", label: "Present, needs parts" },
  { key: "needs_return_visit", label: "Present, needs a return visit" },
  { key: "unable_to_check", label: "Could not check" },
];

/** An answer that must carry the engineer's own words. */
export function insuranceNeedsWords(o: InsuranceOutcome | undefined): boolean {
  return o === "not_present" || o === "unable_to_check";
}

/** The sentence that stops a submit, or null when every item is properly answered. */
export function firstInsuranceGap(items: EngineerInsuranceItem[], answers: Record<string, InsuranceAnswerDraft>): string | null {
  for (const it of items) {
    const a = answers[it.id];
    const name = it.wording.length > 60 ? `${it.wording.slice(0, 57).trimEnd()}...` : it.wording;
    if (!a?.outcome) return `Answer every item before submitting. Still to answer: "${name}"`;
    if (insuranceNeedsWords(a.outcome) && a.note.trim() === "") {
      return a.outcome === "unable_to_check" ? `Say why you could not check: "${name}"` : `Say what you saw, so the office can close it: "${name}"`;
    }
  }
  return null;
}

/** The answers as the completion payload carries them. Empty-string notes and empty photo lists are left out. */
export function insurancePayload(items: EngineerInsuranceItem[], answers: Record<string, InsuranceAnswerDraft>) {
  return items.flatMap((it) => {
    const a = answers[it.id];
    if (!a?.outcome) return [];
    return [
      {
        id: it.id,
        outcome: a.outcome,
        ...(a.note.trim() ? { note: a.note.trim() } : {}),
        ...(a.photoIndexes.length > 0 ? { photoIndexes: [...a.photoIndexes].sort((x, y) => x - y) } : {}),
        ...(a.outcome === "verified_present" ? { severity: (a.severity ?? "advisory") as InsuranceSeverity } : {}),
      },
    ];
  });
}

/** An earlier submission's answer, put back as the starting point (a job the office returned). */
export function seedInsuranceAnswers(items: EngineerInsuranceItem[]): Record<string, InsuranceAnswerDraft> {
  const out: Record<string, InsuranceAnswerDraft> = {};
  for (const it of items) {
    if (it.answer) out[it.id] = { outcome: it.answer.outcome, note: it.answer.note ?? "", photoIndexes: it.answer.photoIndexes, ...(it.answer.severity ? { severity: it.answer.severity } : {}) };
  }
  return out;
}

function ukDay(iso: string | null): string | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
}

export function InsuranceCheckItems({
  items,
  answers,
  onChange,
  uploadedPhotoCount,
}: {
  items: EngineerInsuranceItem[];
  answers: Record<string, InsuranceAnswerDraft>;
  onChange: (id: string, next: InsuranceAnswerDraft) => void;
  /** How many photos the sheet holds that have finished uploading: each can be ticked as backing an item. */
  uploadedPhotoCount: number;
}) {
  const answered = items.filter((i) => answers[i.id]?.outcome).length;
  return (
    <section className="mb-5" aria-labelledby="insurance-check-heading">
      <h2 id="insurance-check-heading" className="font-heading text-lg text-ele-blue">
        Insurance check
      </h2>
      <p className="mb-1 text-sm text-ele-body">
        An insurer's inspector examined this lift and reported the items below. We have not seen them ourselves. Check each one and say what
        you find. This is a check, not a repair.
      </p>
      <p className="mb-3 font-menu text-[11px] uppercase tracking-wider text-ele-body opacity-70" aria-live="polite">
        {answered} of {items.length} answered
      </p>
      {items.length === 0 && <p className="rounded-ele border border-ele-card-border bg-ele-card p-3 text-sm text-ele-body">There are no items on this check. Tell the office.</p>}
      <ul className="space-y-4">
        {items.map((it, idx) => {
          const a: InsuranceAnswerDraft = answers[it.id] ?? { note: "", photoIndexes: [] };
          const set = (patch: Partial<InsuranceAnswerDraft>) => onChange(it.id, { ...a, ...patch });
          const needsWords = insuranceNeedsWords(a.outcome);
          const examined = ukDay(it.examinationDate);
          return (
            <li key={it.id} className="rounded-lg border border-ele-card-border bg-ele-card p-4">
              <p className="font-menu text-[10px] uppercase tracking-wider text-ele-body opacity-70">
                Item {idx + 1}. {it.insurer}
                {examined ? `, examined ${examined}` : ""}
                {it.insurerCategory ? `, their category: ${it.insurerCategory}` : ""}
              </p>
              <p className="mt-1 whitespace-pre-wrap font-body text-sm text-ele-strong">{it.wording}</p>
              <div role="radiogroup" aria-label={`What you found for item ${idx + 1}`} className="mt-3 grid gap-2">
                {INSURANCE_OUTCOME_CHOICES.map((c) => {
                  const on = a.outcome === c.key;
                  return (
                    <button
                      key={c.key}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => set({ outcome: c.key, ...(c.key === "verified_present" ? { severity: a.severity ?? "advisory" } : { severity: undefined }) })}
                      className={`min-h-11 rounded-ele border px-3 py-2 text-left text-sm transition ${on ? "border-ele-green bg-ele-green/15 text-ele-strong" : "border-ele-field-border text-ele-body"}`}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>
              {a.outcome === "verified_present" && (
                <div className="mt-3">
                  <p className="mb-1 text-sm text-ele-body">How serious is it?</p>
                  <div role="radiogroup" aria-label="Category" className="grid grid-cols-2 gap-2">
                    {(
                      [
                        ["advisory", "Advisory"],
                        ["immediate", "Immediate (Category A)"],
                      ] as [InsuranceSeverity, string][]
                    ).map(([key, label]) => {
                      const on = (a.severity ?? "advisory") === key;
                      return (
                        <button
                          key={key}
                          type="button"
                          role="radio"
                          aria-checked={on}
                          onClick={() => set({ severity: key })}
                          className={`min-h-11 rounded-ele border px-3 py-2 text-sm ${on ? "border-ele-purple bg-ele-purple/15 text-ele-strong" : "border-ele-field-border text-ele-body"}`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {a.outcome && (
                <div className="mt-3">
                  <label htmlFor={`ins-note-${it.id}`} className="mb-1 block text-sm text-ele-body">
                    {a.outcome === "unable_to_check" ? "Why you could not check it (required)" : a.outcome === "not_present" ? "What you saw (required)" : "Notes (optional)"}
                  </label>
                  <textarea
                    id={`ins-note-${it.id}`}
                    value={a.note}
                    onChange={(e) => set({ note: e.target.value })}
                    rows={2}
                    aria-required={needsWords}
                    className="w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-field-focus focus:ring-1"
                  />
                </div>
              )}
              {a.outcome && uploadedPhotoCount > 0 && (
                <div className="mt-3">
                  <p className="mb-1 text-sm text-ele-body">Photos that show this item (take them in Photos below, then tick)</p>
                  <div className="flex flex-wrap gap-2">
                    {Array.from({ length: uploadedPhotoCount }, (_, i) => {
                      const on = a.photoIndexes.includes(i);
                      return (
                        <button
                          key={i}
                          type="button"
                          aria-pressed={on}
                          onClick={() => set({ photoIndexes: on ? a.photoIndexes.filter((n) => n !== i) : [...a.photoIndexes, i] })}
                          className={`min-h-11 min-w-11 rounded-ele border px-3 text-sm ${on ? "border-ele-green bg-ele-green/15 text-ele-strong" : "border-ele-field-border text-ele-body"}`}
                        >
                          Photo {i + 1}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
